import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Application, Container, Graphics, Sprite, Text, Texture, type Ticker } from 'pixi.js';
import { playSfx } from '../../effects/sound.js';
import { usePixiScene } from '../pixi/usePixiScene.js';
import { PARKED_AT, SpritePool } from '../pixi/spritePool.js';
import { buildTrajectory, contactPeg, contactTime, sampleTrajectory, type PlinkoTrajectory } from './plinkoTrajectory.js';
import { originalTheme, sceneColor } from '../../theme.js';
import { preferredDensity, preloadPieces, type PieceImages } from '../../assets/sprites.js';
import type { OriginalsConfig } from '../../types.js';

export interface PlinkoDropInput {
  id: string;
  path: ('L' | 'R')[];
  bucket: number;
  multiplierBp: number;
  /** Display-ready win amount ("+฿1.94") floated above the bucket on landing; omit on 0×. */
  winLabel?: string;
}

export interface PlinkoSceneHandle {
  /** Push a resolved server round into the running scene. `onSettle` fires once the ball lands. */
  addDrop: (drop: PlinkoDropInput, onSettle: (drop: PlinkoDropInput) => void) => void;
}

interface PlinkoSceneProps {
  config: OriginalsConfig;
  turbo?: boolean;
}

const STEP_MS = 95;
const TURBO_SCALE = 0.4;
const SETTLE_MS = 200;
const IMPACT_MS = 420;
const PEG_PULSE_MS = 260;
const BURST_MS = 800;
const FLOAT_MS = 950;
const TRAIL_LIFE_MS = 240;
/** Trail dots are laid by distance travelled, not by time, so the comet stays unbroken at any speed or frame rate. */
const TRAIL_SPACING = 0.8; // × ball radius
const TRAIL_MAX_PER_FRAME = 6;
const FLOAT_POOL = 8;
const BURST_RAYS = 10;
/** Sizes the shared effect textures are drawn at, in CSS px, before each use scales them. */
const FX_DOT_PX = 24;
const FX_RING_PX = 64;
const FX_RAY_PX = 128;
const PRIMARY_FALLBACK = 0x7c3aed;
const MIN_BOUNCE_INTERVAL_MS = 35;
const BIG_WIN_BP = 30000; // >= 3x → celebration burst

const EDGE_HOT = 0xff5d3d;
const EDGE_WARM = 0xffa53d;
const MID_WARM = 0xf4c542;
const MID_COOL = 0x3ddc97;
const CENTER_COOL = 0x4aa3ff;
const ZERO_TIER = 0x3b3b4f;
const GOLD = 0xfbbf24;

/** The card-style sprites this scene draws (scripts/art/originals-assets.json → plinko). */
export const PLINKO_PIECES = [
  'board',
  'peg',
  'peg-glow',
  'ball-pink',
  'ball-gold',
  'ball-cyan',
  'bucket-red',
  'bucket-orange',
  'bucket-yellow',
  'bucket-green',
  'bucket-blue',
  'bucket-grey',
  'divider',
  'sparkle',
  'coin',
] as const;
export type PlinkoPiece = (typeof PLINKO_PIECES)[number];

const BALL_PIECES = ['ball-pink', 'ball-gold', 'ball-cyan'] as const;
/** Each ball's own colour, for its comet trail. */
const BALL_TRAIL: Record<(typeof BALL_PIECES)[number], number> = {
  'ball-pink': 0xff8cc0,
  'ball-gold': 0xffd84a,
  'ball-cyan': 0x6ee6f2,
};

/** Where the settle hands the ball over, in gaps below the last peg row (bucketTopY / ballRestY). */
const SETTLE_DROP_GAPS = 1 + 0.18 + 0.3;
/** easeOutCubic's opening speed × the settle's drop, in gaps per step: the drop leaves its last row at this speed. */
const SETTLE_EXIT_SPEED = (3 * SETTLE_DROP_GAPS * STEP_MS) / SETTLE_MS;

/** Board art bounds around the peg field, in peg gaps: top above the hidden apex row, bottom below the cups. */
const BOARD_ABOVE_APEX = 1.45;
/** Under the baskets, inside the tray: the band their multipliers are written on, in peg gaps. */
const LABEL_BAND = 0.52;
/** The tray's lower lip, then the board's own floor and rim below it, in peg gaps: the whole
 * tray sits inside the board, never on its rim. */
const TRAY_LIP = 0.14;
const RIM_BELOW_TRAY = 1.1;
const BOARD_BELOW_CUPS = LABEL_BAND + TRAY_LIP + RIM_BELOW_TRAY;
/** The board art drawn this much wider than tall, so its slanted sides clear the outermost cups. */
const BOARD_STRETCH = 1.14;
/** Basket width in peg gaps; the rest of each slot is the cream pillar between neighbours. */
const BASKET_W = 0.74;
/** Basket height in peg gaps the layout reserves (the art is about as tall as wide, handle included). */
const BASKET_H = 0.9;
/** The card's tray: a raised peach lip round a deeper channel, a pocket under every basket. */
const TRAY_LIP_COLOR = 0xffb27a;
const TRAY_CHANNEL = 0xe06a2c;
const TRAY_POCKET = 0x9c3a14;

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}
function easeOutQuart(t: number): number {
  return 1 - Math.pow(1 - t, 4);
}

/** One query per scene, read every frame: `matchMedia` itself parses a new query each call. */
function reducedMotionQuery(): MediaQueryList | null {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;
}

function parseHex(input: string | undefined, fb: number): number {
  if (!input) return fb;
  const h = input.trim().replace(/^#/, '');
  return /^[0-9a-fA-F]{6}$/.test(h) ? parseInt(h, 16) : fb;
}

function lighten(color: number, amount: number): number {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  const lr = Math.round(r + (255 - r) * amount);
  const lg = Math.round(g + (255 - g) * amount);
  const lb = Math.round(b + (255 - b) * amount);
  return (lr << 16) | (lg << 8) | lb;
}

/** "5.6×", "1.1×", "0.2×", "0×" — trims trailing zeros, never NaN. */
export function formatMultiplierLabel(bp: number): string {
  const v = bp / 10000;
  const s = v.toFixed(2).replace(/\.?0+$/, '');
  return `${s === '' ? '0' : s}×`;
}

/** CSS hex for a bucket colour — shared with the DOM recent-hits rail. */
export function bucketColorCss(bp: number, bucketIdx: number, total: number): string {
  return `#${bucketColor(bp, bucketIdx, total).toString(16).padStart(6, '0')}`;
}

/** The cup sprite for a bucket: the same tier as {@link bucketColor}, so the recent-hits rail still agrees. */
export function bucketPiece(bp: number, bucketIdx: number, total: number): PlinkoPiece {
  const c = bucketColor(bp, bucketIdx, total);
  if (c === EDGE_HOT) return 'bucket-red';
  if (c === EDGE_WARM) return 'bucket-orange';
  if (c === MID_WARM) return 'bucket-yellow';
  if (c === MID_COOL) return 'bucket-green';
  if (c === CENTER_COOL) return 'bucket-blue';
  return 'bucket-grey';
}

/**
 * Colour for a bucket by distance from centre and payout tier. Edges run hot
 * (red/orange) like real Plinko, the centre runs cool (teal/blue); a zero-payout
 * bucket is a flat grey regardless of position.
 */
export function bucketColor(bp: number, bucketIdx: number, total: number): number {
  if (bp <= 0) return ZERO_TIER;
  const center = (total - 1) / 2;
  const d = center > 0 ? Math.abs(bucketIdx - center) / center : 0;
  if (d >= 0.85) return EDGE_HOT;
  if (d >= 0.6) return EDGE_WARM;
  if (d >= 0.4) return MID_WARM;
  if (d >= 0.2) return MID_COOL;
  return CENTER_COOL;
}

interface ActiveBall {
  id: string;
  path: ('L' | 'R')[];
  bucket: number;
  onSettle: (drop: PlinkoDropInput) => void;
  drop: PlinkoDropInput;
  gfx: Container;
  trailColor: number;
  phase: 'dropping' | 'settling' | 'done';
  traj: PlinkoTrajectory;
  /** Steps into the drop; the drop ends at `traj.rows`. */
  progress: number;
  segment: number;
  nextContact: number;
  trailX: number;
  trailY: number;
  settleElapsed: number;
  settleFromX: number;
  settleFromY: number;
  settleToX: number;
  settleToY: number;
}

interface Spark {
  gfx: Sprite;
  spin: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
}

interface Burst {
  elapsed: number;
  ring: Sprite;
  rays: Sprite[];
}

interface TrailDot {
  gfx: Sprite;
  elapsed: number;
}

interface FloatLabel {
  gfx: Text;
  fromY: number;
  elapsed: number;
}

interface FxTextures {
  dot: Texture;
  ring: Texture;
  ray: Texture;
}

function makeFxTextures(app: Application): FxTextures {
  const bake = (g: Graphics): Texture => {
    const tex = app.renderer.generateTexture({ target: g, resolution: 2, antialias: true });
    g.destroy();
    return tex;
  };
  const half = 0.06;
  return {
    dot: bake(new Graphics().circle(0, 0, FX_DOT_PX / 2).fill({ color: 0xffffff })),
    ring: bake(new Graphics().circle(0, 0, FX_RING_PX).stroke({ width: 6, color: 0xffffff })),
    // A thin wedge from its tip: anchored at (0, 0.5) and rotated, it is one burst ray.
    ray: bake(
      new Graphics()
        .moveTo(0, 0)
        .lineTo(Math.cos(-half) * FX_RAY_PX, Math.sin(-half) * FX_RAY_PX)
        .lineTo(Math.cos(half) * FX_RAY_PX, Math.sin(half) * FX_RAY_PX)
        .closePath()
        .fill({ color: 0xffffff }),
    ),
  };
}

/** Fresh, uncached textures per scene: the scene's teardown destroys them, the decoded images stay cached. */
function toTextures(imgs: PieceImages<PlinkoPiece>): Record<PlinkoPiece, Texture> {
  const out = {} as Record<PlinkoPiece, Texture>;
  for (const piece of PLINKO_PIECES) {
    out[piece] = Texture.from({ resource: imgs[piece], autoGenerateMipmaps: true }, true);
  }
  return out;
}

export const PlinkoScene = forwardRef<PlinkoSceneHandle, PlinkoSceneProps>(function PlinkoScene(
  { config, turbo = false },
  ref,
) {
  const { t } = useTranslation('originals');

  const presenterRef = useRef(config.presenterConfig as { rows: number; buckets: number[] });
  presenterRef.current = config.presenterConfig as { rows: number; buckets: number[] };
  const turboRef = useRef(turbo);
  turboRef.current = turbo;

  // Bridge: usePixiScene captures onReady once; the live add-drop fn is published here so the
  // imperative handle (created before the scene mounts) forwards to whatever the scene installed.
  const addDropRef = useRef<PlinkoSceneHandle['addDrop'] | null>(null);

  useImperativeHandle(
    ref,
    () => ({
      addDrop: (drop, onSettle) => addDropRef.current?.(drop, onSettle),
    }),
    [],
  );

  const onReady = useRef((app: Application, host: HTMLDivElement) => {
    const cfg = presenterRef.current;
    const rows = Number(cfg?.rows ?? 12);
    const primary = parseHex(
      getComputedStyle(host).getPropertyValue('--primary').trim() || undefined,
      PRIMARY_FALLBACK,
    );
    const primaryGlow = lighten(primary, 0.4);
    const theme = originalTheme('plinko');
    const pegColor = theme ? sceneColor(theme.glow) : 0xffffff;
    // The card-style sprites, once decoded. Until then — or for good, if a file never
    // arrives — the scene draws its vector board, so a missing asset never blanks the game.
    let art: Record<PlinkoPiece, Texture> | null = null;
    let ballSeq = 0;
    let disposed = false;
    const reducedMq = reducedMotionQuery();
    const prefersReducedMotion = () => reducedMq?.matches ?? false;

    const root = new Container();
    app.stage.addChild(root);
    const backdrop = new Graphics();
    const boardLayer = new Container();
    const glowLayer = new Container();
    const pegs = new Container();
    const trailLayer = new Container();
    const ballsLayer = new Container({ label: 'plinko-balls' });
    const bucketsContainer = new Container();
    const labelsContainer = new Container();
    const burstLayer = new Container();
    const sparksLayer = new Container();
    const floatsLayer = new Container();
    const glowPool = new SpritePool(glowLayer);
    const trailPool = new SpritePool(trailLayer);
    const burstPool = new SpritePool(burstLayer);
    const sparkPool = new SpritePool(sparksLayer);
    const floatPool: Text[] = [];
    const sparkFree: Spark[] = [];
    // Plain white shapes rendered once and tinted per use: a fresh Graphics per trail dot,
    // spark or burst frame would be tessellated (and later destroyed) every time.
    const fx = makeFxTextures(app);
    // Balls render BEHIND the bucket slots so a landing ball sinks into (and is occluded by)
    // its slot instead of resting on top of the artwork.
    root.addChild(
      backdrop,
      boardLayer,
      glowLayer,
      pegs,
      trailLayer,
      ballsLayer,
      bucketsContainer,
      labelsContainer,
      burstLayer,
      sparksLayer,
      floatsLayer,
    );

    let W = 100,
      H = 100,
      pegGap = 10,
      topY = 0,
      ballR = 4,
      pegR = 2;
    let lastBounceAt = 0;
    const pegGfx: Container[] = [];
    const pegGlow: (Sprite | null)[] = [];
    const pegX: number[] = [];
    const pegY: number[] = [];
    const pegPulse: number[] = [];
    const bucketGfx: Container[] = [];
    const bucketLabels: Text[] = [];
    const bucketBaseY: number[] = [];
    const bucketImpact: number[] = [];
    const balls: ActiveBall[] = [];
    const sparks: Spark[] = [];
    const bursts: Burst[] = [];
    const trails: TrailDot[] = [];
    const floats: FloatLabel[] = [];
    // Consecutive paying landings; drives the rising-pitch win pop (resets on a 0× bucket).
    let winCombo = 0;

    const pegIndex = (r: number, c: number) => (r * (r + 1)) / 2 + c;

    function bucketX(idx: number): number {
      return (idx - rows / 2) * pegGap;
    }
    function bucketTopY(): number {
      return topY + rows * pegGap + pegGap * 0.18;
    }
    function ballRestY(): number {
      // Just inside the slot mouth: the ball visibly sinks INTO the bucket (it renders
      // behind the slot artwork) rather than perching above it.
      return bucketTopY() + pegGap * 0.3;
    }

    function sizeBall(view: Container) {
      if (view instanceof Graphics) {
        drawBall(view);
        return;
      }
      // The sprite sits inside a plain container: the settle squash scales the container,
      // which a sprite's own scale (it carries the sprite's size) could not take.
      const sprite = view.children[0];
      if (sprite instanceof Sprite) sprite.width = sprite.height = ballR * 2.7;
    }

    function drawBall(g: Graphics) {
      g.clear()
        .circle(0, 0, ballR * 1.45)
        .fill({ color: primaryGlow, alpha: 0.2 })
        .circle(0, 0, ballR)
        .fill({ color: primary })
        .circle(-ballR * 0.32, -ballR * 0.32, ballR * 0.42)
        .fill({ color: 0xffffff, alpha: 0.55 });
    }

    function layout() {
      W = app.screen.width;
      H = app.screen.height;
      // Fill the stage: width caps the peg spacing when wide, height when tall. The apex peg
      // (row 0) is hidden, so the VISIBLE board runs from the 2-peg row down to the bucket
      // floor — fit that, and let the apex + spawn point sit one and two gaps above it (clipped
      // off the top) instead of reserving an empty band for a peg that isn't drawn.
      // With the card art the whole board (apex to cup tray) must show, so it fits a taller block.
      const boardH = BOARD_ABOVE_APEX + rows + 0.18 + BASKET_H + BOARD_BELOW_CUPS;
      pegGap = art
        ? Math.min((W * 0.97) / (boardH * BOARD_STRETCH), (H * 0.97) / boardH)
        : Math.min((W * 0.95) / (rows + 0.9), (H * 0.96) / (rows + 0.5));
      ballR = Math.max(2.5, pegGap * (art ? 0.23 : 0.17));
      pegR = Math.max(2, pegGap * 0.11);
      const slotW = pegGap * 0.94;
      const basketW = pegGap * BASKET_W;
      const bucketH = art ? pegGap * BASKET_H : pegGap * 0.62;
      if (art) {
        // Centre the board art; the apex peg row sits BOARD_ABOVE_APEX gaps under its tip.
        topY = Math.max(0, (H - boardH * pegGap) / 2) + BOARD_ABOVE_APEX * pegGap;
      } else {
        // Centre the visible block (first drawn peg row → bucket floor) in the stage.
        const visibleH = (rows - 0.2) * pegGap;
        const offsetY = Math.max(0, (H - visibleH) / 2);
        // First visible (2-peg) row sits at offsetY; the hidden apex is one gap above, off-screen.
        topY = offsetY - pegGap;
      }
      root.position.set(W / 2, 0);
      backdrop.visible = !art;

      boardLayer.removeChildren();
      if (art) {
        const board = new Sprite(art.board);
        const top = topY - BOARD_ABOVE_APEX * pegGap;
        const bottom = bucketTopY() + bucketH + BOARD_BELOW_CUPS * pegGap;
        board.anchor.set(0.5, 0);
        board.position.set(0, top);
        board.height = bottom - top;
        board.width = board.height * (art.board.width / art.board.height) * BOARD_STRETCH;
        boardLayer.addChild(board);
      }

      // backdrop glow centred on the peg field
      const cy = topY + (rows * pegGap) / 2;
      const gr = rows * pegGap * 0.85;
      backdrop
        .clear()
        .circle(0, cy, gr)
        .fill({ color: primary, alpha: 0.06 })
        .circle(0, cy, gr * 0.66)
        .fill({ color: primary, alpha: 0.07 });

      pegs.removeChildren();
      for (const g of pegGlow) if (g) glowPool.release(g);
      pegGfx.length = 0;
      pegGlow.length = 0;
      pegPulse.length = 0;
      pegX.length = 0;
      pegY.length = 0;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c <= r; c++) {
          const x = (c - r / 2) * pegGap;
          const y = topY + r * pegGap;
          let peg: Container;
          if (art) {
            // The glow is lent from a pool only while the peg pulses: 78 idle glows at zero
            // alpha would still be drawn, each a full quad of blended pixels.
            peg = new Container();
            const stud = new Sprite(art.peg);
            stud.anchor.set(0.5, 0.55);
            stud.height = pegGap * 0.4;
            stud.width = stud.height * (art.peg.width / art.peg.height);
            peg.addChild(stud);
          } else {
            peg = new Graphics()
              .circle(0, 0, pegR * 2.3)
              .fill({ color: pegColor, alpha: 0.14 })
              .circle(0, 0, pegR)
              .fill({ color: pegColor, alpha: 0.95 });
          }
          peg.position.set(x, y);
          // Hide the single apex peg (row 0). Kept in the arrays so pegIndex/pegPulse
          // stay aligned; the ball drops into the gap between the first visible (2-peg) row.
          peg.visible = r > 0;
          pegs.addChild(peg);
          pegGfx.push(peg);
          pegGlow.push(null);
          pegPulse.push(0);
          pegX.push(x);
          pegY.push(y);
        }
      }

      bucketsContainer.removeChildren();
      labelsContainer.removeChildren();
      bucketGfx.length = 0;
      bucketLabels.length = 0;
      bucketBaseY.length = 0;
      bucketImpact.length = 0;
      const by = bucketTopY();
      const buckets = cfg.buckets ?? [];
      const total = rows + 1;
      const slotH = bucketH;
      const fontSize = Math.max(9, Math.min(pegGap * 0.34, 16));
      const slotR = Math.max(3, pegGap * 0.12);
      const mouthRx = slotW * 0.5; // full slot width — the mouth spans rim to rim
      const mouthRy = slotH * 0.3;
      if (art) {
        // The card's tray, inside the board's foot: a raised lip round a deeper channel, and a
        // pocket under every basket so each one sits in its own slot.
        const trayW = total * pegGap + pegGap * 0.5;
        const trayTop = by - pegGap * 0.16;
        const trayBottom = by + slotH + LABEL_BAND * pegGap + TRAY_LIP * pegGap;
        const r = pegGap * 0.32;
        const inset = pegGap * 0.09;
        const tray = new Graphics()
          .roundRect(-trayW / 2, trayTop, trayW, trayBottom - trayTop, r)
          .fill({ color: TRAY_LIP_COLOR })
          .roundRect(-trayW / 2 + inset, trayTop + inset, trayW - inset * 2, trayBottom - trayTop - inset * 2, r * 0.75)
          .fill({ color: TRAY_CHANNEL })
          // the lip's top edge catches the light, its foot sits in shade
          .roundRect(-trayW / 2 + r * 0.5, trayTop + inset * 0.25, trayW - r, inset * 0.45, inset * 0.2)
          .fill({ color: 0xffffff, alpha: 0.45 })
          .roundRect(-trayW / 2 + inset, trayBottom - inset * 1.6, trayW - inset * 2, inset * 0.7, inset * 0.3)
          .fill({ color: 0x5a1e06, alpha: 0.18 });
        for (let b = 0; b < total; b++) {
          const x = bucketX(b);
          tray
            .roundRect(x - pegGap * 0.42, by - pegGap * 0.04, pegGap * 0.84, slotH + pegGap * 0.06, pegGap * 0.14)
            .fill({ color: TRAY_POCKET, alpha: 0.38 })
            .roundRect(x - pegGap * 0.42, by - pegGap * 0.04, pegGap * 0.84, pegGap * 0.12, pegGap * 0.06)
            .fill({ color: 0x000000, alpha: 0.18 });
        }
        bucketsContainer.addChild(tray);
      }
      for (let b = 0; b < total; b++) {
        const bp = (buckets[b] as number | undefined) ?? 0;
        const color = bucketColor(bp, b, total);
        const slot = art ? new Container() : new Graphics();
        slot.position.set(bucketX(b), by + slotH / 2);
        slot.pivot.set(0, slotH / 2);
        if (art) {
          const tex = art[bucketPiece(bp, b, total)];
          const cup = new Sprite(tex);
          // Bottom-aligned in the slot, as wide as the slot allows, never stretched.
          cup.anchor.set(0.5, 1);
          const scale = Math.min(basketW / tex.width, slotH / tex.height);
          cup.scale.set(scale);
          cup.position.set(0, slotH);
          slot.addChild(cup);
        }
        // landscape slot body + darker base band (3D depth) + half-ellipse pit
        // mouth: flat edge on the slot's top line, bulging down INTO the slot
        // (cubic control offset 4/3·ry traces the lower half of an ellipse)
        if (slot instanceof Graphics) slot
          .roundRect(-slotW / 2, 0, slotW, slotH, slotR)
          .fill({ color, alpha: bp > 0 ? 1 : 0.55 })
          .roundRect(-slotW / 2, slotH * 0.66, slotW, slotH * 0.34, slotR)
          .fill({ color: 0x000000, alpha: bp > 0 ? 0.22 : 0.12 })
          .moveTo(-mouthRx, 0)
          .bezierCurveTo(-mouthRx, (mouthRy * 4) / 3, mouthRx, (mouthRy * 4) / 3, mouthRx, 0)
          .closePath()
          .fill({ color: 0x000000, alpha: bp > 0 ? 0.38 : 0.24 });
        bucketsContainer.addChild(slot);
        bucketGfx.push(slot);
        bucketBaseY.push(by + slotH / 2);
        bucketImpact.push(0);

        // White text reads on every tier; a dark stroke + shadow keeps it crisp on the bright slots.
        const label = new Text({
          text: formatMultiplierLabel(bp),
          style: {
            fontFamily: 'system-ui, sans-serif',
            fontSize,
            fontWeight: '800',
            fill: 0xffffff,
            align: 'center',
            stroke: { color: 0x10101a, width: Math.max(2, fontSize * 0.16), alpha: 0.85 },
            dropShadow: { color: 0x000000, alpha: 0.35, blur: 2, distance: 1, angle: Math.PI / 2 },
          },
        });
        label.anchor.set(0.5);
        // shrink-to-fit: wide labels ("8.18×") must never spill the slot
        const maxLabelW = slotW * 0.86;
        if (label.width > maxLabelW) label.scale.set(maxLabelW / label.width);
        // With the card art the value sits in the tray's band under its basket, so the basket
        // stays whole and the number stays inside the board.
        label.position.set(bucketX(b), art ? by + slotH + (LABEL_BAND * pegGap) / 2 + pegGap * 0.04 : by + slotH * 0.58);
        labelsContainer.addChild(label);
        bucketLabels.push(label);
      }

      if (art) {
        // Cream pillars on both sides of every slot, standing in front of the baskets' edges.
        for (let d = 0; d <= total; d++) {
          const pillar = new Sprite(art.divider);
          pillar.anchor.set(0.5, 1);
          pillar.height = slotH * 1.06;
          pillar.width = Math.max(pegGap * 0.17, pillar.height * (art.divider.width / art.divider.height));
          pillar.position.set(bucketX(d) - pegGap / 2, by + slotH + pegGap * 0.05);
          bucketsContainer.addChild(pillar);
        }
      }

      for (const ball of balls) sizeBall(ball.gfx);
    }

    layout();
    const onResize = () => layout();
    app.renderer.on('resize', onResize);

    function makeBall(): { view: Container; trailColor: number } {
      let view: Container;
      let trailColor = primaryGlow;
      if (art) {
        const piece = BALL_PIECES[ballSeq++ % BALL_PIECES.length]!;
        const s = new Sprite(art[piece]);
        s.anchor.set(0.5);
        view = new Container();
        view.addChild(s);
        trailColor = BALL_TRAIL[piece];
      } else {
        view = new Graphics();
      }
      sizeBall(view);
      view.position.set(0, topY - pegGap);
      ballsLayer.addChild(view);
      return { view, trailColor };
    }

    function spawnSparks(x: number, y: number, color: number, n: number) {
      for (let i = 0; i < n; i++) {
        const angle = -Math.PI + (Math.PI * i) / (n - 1) + (Math.random() - 0.5) * 0.4;
        const speed = 1.5 + Math.random() * 3.5;
        let sp: Sprite;
        if (art) {
          // Gold coins and glints instead of dots: the win reads like the card's sparkle.
          const coin = i % 3 === 0;
          sp = sparkPool.acquire(coin ? art.coin : art.sparkle);
          sp.width = sp.height = ballR * (coin ? 2.4 : 3.6);
          // Pooled sprites keep their last tint and blend: set both every time. Glints add
          // light (they brighten whatever they cross); coins stay their own gold.
          sp.tint = coin || color === GOLD ? 0xffffff : lighten(color, 0.55);
          sp.blendMode = coin ? 'normal' : 'add';
        } else {
          sp = sparkPool.acquire(fx.dot);
          sp.width = sp.height = Math.max(1.5, ballR * 0.4) * 2;
          sp.tint = color;
          sp.blendMode = 'normal';
        }
        sp.position.set(x, y);
        const s = sparkFree.pop() ?? { gfx: sp, spin: 0, x: 0, y: 0, vx: 0, vy: 0, life: 0, maxLife: 0 };
        s.gfx = sp;
        s.x = x;
        s.y = y;
        s.vx = Math.cos(angle) * speed;
        s.vy = Math.sin(angle) * speed - 1.5;
        s.spin = art && !prefersReducedMotion() ? (Math.random() - 0.5) * 0.3 : 0;
        s.life = 0;
        s.maxLife = 26 + Math.random() * 16;
        sparks.push(s);
      }
    }

    function addDrop(drop: PlinkoDropInput, onSettle: (d: PlinkoDropInput) => void) {
      const { view: gfx, trailColor } = makeBall();
      balls.push({
        id: drop.id,
        path: drop.path,
        bucket: drop.bucket,
        onSettle,
        drop,
        gfx,
        trailColor,
        phase: 'dropping',
        traj: buildTrajectory(drop.path, SETTLE_EXIT_SPEED),
        progress: 0,
        segment: 0,
        nextContact: 0,
        trailX: gfx.position.x,
        trailY: gfx.position.y,
        settleElapsed: 0,
        settleFromX: 0,
        settleFromY: 0,
        settleToX: 0,
        settleToY: 0,
      });
    }
    addDropRef.current = addDrop;

    function floatText(): Text {
      const parked = floatPool.find((t) => t.alpha === 0);
      if (parked) return parked;
      const text = new Text({ text: '', style: { fontFamily: 'system-ui, sans-serif', fontWeight: '800', align: 'center' } });
      text.anchor.set(0.5, 1);
      floatsLayer.addChild(text);
      if (floatPool.length < FLOAT_POOL) floatPool.push(text);
      return text;
    }

    function spawnFloat(x: number, label: string, big: boolean) {
      const fontSize = Math.max(11, Math.min(pegGap * 0.46, 20));
      const text = floatText();
      text.style.fontSize = fontSize;
      text.style.fill = big ? GOLD : MID_COOL;
      text.style.stroke = { color: 0x10101a, width: Math.max(2, fontSize * 0.18), alpha: 0.9 };
      text.style.dropShadow = { color: 0x000000, alpha: 0.4, blur: 2, distance: 1, angle: Math.PI / 2 };
      text.text = label;
      text.alpha = 1;
      const fromY = bucketTopY() - pegGap * 0.35;
      text.position.set(x, fromY);
      floats.push({ gfx: text, fromY, elapsed: 0 });
    }

    function spawnBurst(x: number, y: number, color: number) {
      const ring = burstPool.acquire(fx.ring);
      ring.tint = color;
      ring.position.set(x, y);
      const rays: Sprite[] = [];
      for (let k = 0; k < BURST_RAYS; k++) {
        const ray = burstPool.acquire(fx.ray, 0, 0.5);
        ray.tint = color;
        ray.position.set(x, y);
        rays.push(ray);
      }
      bursts.push({ elapsed: 0, ring, rays });
    }

    function fireImpact(ball: ActiveBall) {
      bucketImpact[ball.bucket] = 1;
      const buckets = cfg.buckets ?? [];
      const bp = (buckets[ball.bucket] as number | undefined) ?? 0;
      const color = bucketColor(bp, ball.bucket, rows + 1);
      const big = bp >= BIG_WIN_BP;
      const reduced = prefersReducedMotion();
      const now = performance.now();
      winCombo = bp > 0 ? winCombo + 1 : 0;
      if (now - lastBounceAt > MIN_BOUNCE_INTERVAL_MS) {
        // Wins climb in pitch the longer the paying streak runs — the slot-machine pull
        // that makes back-to-back drops feel like building momentum.
        playSfx(big ? 'bigWin' : bp > 0 ? 'tilePop' : 'click', winCombo);
        lastBounceAt = now;
      }
      spawnSparks(bucketX(ball.bucket), bucketTopY(), big ? GOLD : color, reduced ? 5 : big ? 22 : 12);
      if (big) spawnBurst(bucketX(ball.bucket), bucketTopY(), GOLD);
      if (ball.drop.winLabel && bp > 0 && !reduced) spawnFloat(bucketX(ball.bucket), ball.drop.winLabel, big);
      ball.onSettle(ball.drop);
    }

    function pulsePeg(row: number, col: number) {
      if (row < 0 || col < 0 || col > row) return;
      const idx = pegIndex(row, col);
      if (idx < 0 || idx >= pegPulse.length) return;
      pegPulse[idx] = 1;
      if (art && pegGfx[idx]!.visible && !pegGlow[idx]) {
        const glow = glowPool.acquire(art['peg-glow']);
        // Added light, so a struck peg flares brighter than the board instead of veiling it.
        glow.blendMode = 'add';
        glow.position.set(pegX[idx]!, pegY[idx]!);
        glow.alpha = 0;
        pegGlow[idx] = glow;
      }
    }

    function layTrail(ball: ActiveBall, x: number, y: number, reduced: boolean) {
      const dx = x - ball.trailX;
      const dy = y - ball.trailY;
      const dist = Math.hypot(dx, dy);
      const spacing = Math.max(1, ballR * TRAIL_SPACING);
      if (reduced || dist < spacing) return;
      const n = Math.min(TRAIL_MAX_PER_FRAME, Math.floor(dist / spacing));
      const size = (ballR * 0.55 * 2) / FX_DOT_PX;
      for (let j = 1; j <= n; j++) {
        const f = (j * spacing) / dist;
        const dot = trailPool.acquire(fx.dot);
        dot.tint = ball.trailColor;
        dot.position.set(ball.trailX + dx * f, ball.trailY + dy * f);
        dot.scale.set(size);
        dot.alpha = 0.35;
        trails.push({ gfx: dot, elapsed: 0 });
      }
      // Skip ahead when a long frame outran the per-frame cap, rather than flooding dots.
      const f = n >= TRAIL_MAX_PER_FRAME ? 1 : (n * spacing) / dist;
      ball.trailX += dx * f;
      ball.trailY += dy * f;
    }

    const pt = { x: 0, y: 0 };

    const tick = (tk: Ticker) => {
      const dt = tk.deltaMS;
      const now = performance.now();
      const reduced = prefersReducedMotion();
      const motionScale = reduced ? 0.6 : 1;
      const turboScale = turboRef.current ? TURBO_SCALE : 1;
      const stepDur = STEP_MS * motionScale * turboScale;
      const settleDur = SETTLE_MS * motionScale * turboScale;

      // ---- balls ----
      for (let i = balls.length - 1; i >= 0; i--) {
        const ball = balls[i]!;
        let settleDt = dt;
        if (ball.phase === 'dropping') {
          const traj = ball.traj;
          ball.progress += dt / stepDur;
          // A long frame can carry the ball past several pegs: flash every one of them.
          while (ball.nextContact < traj.rows && ball.progress >= contactTime(traj, ball.nextContact)) {
            const peg = contactPeg(traj, ball.nextContact);
            pulsePeg(peg.row, peg.col);
            ball.nextContact += 1;
            if (now - lastBounceAt > MIN_BOUNCE_INTERVAL_MS) {
              playSfx('ballBounce');
              lastBounceAt = now;
            }
          }
          if (ball.progress >= traj.rows) {
            ball.phase = 'settling';
            ball.settleElapsed = 0;
            ball.settleFromX = (traj.col[traj.rows]! * pegGap) / 2;
            ball.settleFromY = topY + (traj.rows - 1) * pegGap;
            ball.settleToX = bucketX(ball.bucket);
            ball.settleToY = ballRestY();
            // The settle picks up the time this frame ran past the drop, so the hand-off is seamless.
            settleDt = (ball.progress - traj.rows) * stepDur;
          } else {
            ball.segment = sampleTrajectory(traj, ball.progress, pt, ball.segment);
            const x = pt.x * pegGap;
            const y = topY + pt.y * pegGap;
            ball.gfx.position.set(x, y);
            layTrail(ball, x, y, reduced);
          }
        }
        if (ball.phase === 'settling') {
          ball.settleElapsed += settleDt;
          const tn = Math.min(1, ball.settleElapsed / settleDur);
          const eased = easeOutCubic(tn);
          const squash = tn > 0.7 ? 1 - 0.35 * Math.sin(((tn - 0.7) / 0.3) * Math.PI) : 1;
          ball.gfx.position.set(
            ball.settleFromX + (ball.settleToX - ball.settleFromX) * eased,
            ball.settleFromY + (ball.settleToY - ball.settleFromY) * eased,
          );
          ball.gfx.scale.set(1 + (1 - squash) * 0.6, squash);
          // fade as it sinks behind the slot so semi-transparent (0×) slots don't show it through
          ball.gfx.alpha = 1 - Math.max(0, (tn - 0.55) / 0.45) * 0.95;
          if (tn >= 1) {
            ball.gfx.scale.set(1, 1);
            ball.phase = 'done';
            fireImpact(ball);
            ballsLayer.removeChild(ball.gfx);
            ball.gfx.destroy();
            balls.splice(i, 1);
          }
        }
      }

      // ---- peg pulses (scale + brighten the struck peg) ----
      for (let p = 0; p < pegGfx.length; p++) {
        if (pegPulse[p]! > 0) {
          const v = pegPulse[p]!;
          const peg = pegGfx[p]!;
          const glow = pegGlow[p];
          if (art) {
            // The struck stud lights up from inside, like the card's glowing pegs.
            peg.scale.set(1 + 0.3 * v);
            if (glow) {
              glow.width = glow.height = pegGap * 0.95 * (1 + 0.3 * v);
              glow.alpha = v;
            }
          } else {
            peg.scale.set(1 + 1.1 * v);
            peg.tint = v > 0.4 ? primaryGlow : 0xffffff;
            peg.alpha = 0.9 + 0.1 * v;
          }
          pegPulse[p] = Math.max(0, v - dt / PEG_PULSE_MS);
          if (pegPulse[p]! <= 0) {
            peg.scale.set(1);
            if (glow) {
              glowPool.release(glow);
              pegGlow[p] = null;
            }
            if (!art) {
              peg.tint = 0xffffff;
              peg.alpha = 0.9;
            }
          }
        }
      }

      // ---- bucket impact pulses (scale-pop + brighten) ----
      for (let b = 0; b < bucketGfx.length; b++) {
        const p = bucketImpact[b]!;
        if (p > 0) {
          const e = easeOutQuart(1 - p);
          const slot = bucketGfx[b]!;
          const pop = 1 + 0.35 * (1 - e);
          slot.scale.set(1 + 0.12 * (1 - e), pop);
          slot.position.y = bucketBaseY[b]! + pegGap * 0.18 * (1 - e);
          slot.tint = 0xffffff;
          slot.alpha = 1;
          const label = bucketLabels[b];
          if (label) label.scale.set(1 + 0.22 * (1 - e));
          bucketImpact[b] = Math.max(0, p - dt / IMPACT_MS);
          if (bucketImpact[b]! <= 0) {
            slot.scale.set(1, 1);
            slot.position.y = bucketBaseY[b]!;
            slot.tint = 0xffffff;
            slot.alpha = (cfg.buckets?.[b] ?? 0) > 0 || art ? 1 : 0.55;
            label?.scale.set(1);
          }
        }
      }

      // ---- big-win bursts (expanding ring + rays from the bucket) ----
      for (let i = bursts.length - 1; i >= 0; i--) {
        const burst = bursts[i]!;
        burst.elapsed += dt;
        const pn = burst.elapsed / BURST_MS;
        if (pn >= 1) {
          burstPool.release(burst.ring);
          for (const ray of burst.rays) burstPool.release(ray);
          bursts.splice(i, 1);
          continue;
        }
        const fade = 1 - pn;
        burst.ring.scale.set((pegGap * (0.6 + pn * 3.2)) / FX_RING_PX);
        burst.ring.alpha = 0.6 * fade;
        const len = (pegGap * (1.2 + pn * 2.4)) / FX_RAY_PX;
        for (let k = 0; k < burst.rays.length; k++) {
          const ray = burst.rays[k]!;
          ray.rotation = (k / burst.rays.length) * Math.PI * 2 + pn * 0.5;
          ray.scale.set(len);
          ray.alpha = 0.3 * fade;
        }
      }

      // ---- trail dots (shrink + fade) ----
      const dotSize = (ballR * 0.55 * 2) / FX_DOT_PX;
      for (let i = trails.length - 1; i >= 0; i--) {
        const dot = trails[i]!;
        dot.elapsed += dt;
        const pn = dot.elapsed / TRAIL_LIFE_MS;
        if (pn >= 1) {
          trailPool.release(dot.gfx);
          trails[i] = trails[trails.length - 1]!;
          trails.pop();
          continue;
        }
        dot.gfx.alpha = 0.35 * (1 - pn);
        dot.gfx.scale.set(dotSize * (1 - pn * 0.6));
      }

      // ---- floating win labels (rise + fade) ----
      for (let i = floats.length - 1; i >= 0; i--) {
        const f = floats[i]!;
        f.elapsed += dt;
        const pn = f.elapsed / FLOAT_MS;
        if (pn >= 1) {
          f.gfx.alpha = 0;
          f.gfx.position.set(PARKED_AT, PARKED_AT);
          if (!floatPool.includes(f.gfx)) {
            floatsLayer.removeChild(f.gfx);
            f.gfx.destroy();
          }
          floats.splice(i, 1);
          continue;
        }
        f.gfx.position.y = f.fromY - easeOutCubic(pn) * pegGap * 1.5;
        f.gfx.alpha = pn < 0.55 ? 1 : 1 - (pn - 0.55) / 0.45;
      }

      // ---- sparks ----
      for (let i = sparks.length - 1; i >= 0; i--) {
        const s = sparks[i]!;
        s.life += dt / 16.67;
        if (s.life >= s.maxLife) {
          sparkPool.release(s.gfx);
          sparks[i] = sparks[sparks.length - 1]!;
          sparks.pop();
          sparkFree.push(s);
          continue;
        }
        s.vy += 0.22;
        s.x += s.vx;
        s.y += s.vy;
        s.gfx.position.set(s.x, s.y);
        s.gfx.rotation += s.spin;
        s.gfx.alpha = Math.max(0, 1 - s.life / s.maxLife);
      }

      // backdrop breathe
      if (!art) backdrop.alpha = 1 + 0.25 * Math.sin(now / 900);
    };

    app.ticker.add(tick);

    void preloadPieces('plinko', PLINKO_PIECES, preferredDensity(window.devicePixelRatio)).then((imgs) => {
      if (!imgs || disposed) return;
      art = toTextures(imgs);
      layout();
    });

    return () => {
      disposed = true;
      if (art) for (const tex of Object.values(art)) tex.destroy(true);
      art = null;
      for (const tex of Object.values(fx)) tex.destroy(true);
      addDropRef.current = null;
      app.ticker.remove(tick);
      app.renderer.off('resize', onResize);
    };
  }).current;

  // Start fetching the art with the component, ahead of the Pixi init the scene waits on.
  useEffect(() => {
    void preloadPieces('plinko', PLINKO_PIECES, preferredDensity(window.devicePixelRatio));
  }, []);

  const hostRef = usePixiScene(onReady);

  return (
    <div className="relative w-full overflow-hidden lg:h-[clamp(420px,66vh,620px)] originals-scene-fit [--orig-reserve:620px]">
      <div ref={hostRef} className="absolute inset-0" aria-hidden />
      <span className="sr-only">{t('plinko.drop')}</span>
    </div>
  );
});
